import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { automationProjectionQueryOptions } from '@/lib/api/automations.js';

import { AutomationShareDialog } from './AutomationShareDialog.js';
import { useAutomationWorkspace } from './useAutomationWorkspace.js';

/**
 * Opens `AutomationShareDialog` for an automation known only by its id — every surface but the
 * detail page (the chat header, the sidebar row menu, the `/scheduled` row menu) has an automation
 * id in hand and nothing else. Reads the audience projection, which now carries the automation's
 * dedicated-chat id directly; a caller that already has that id (from its own list row or
 * projection) passes it as `conversationId` so the dialog need not wait on this component's own
 * projection read to know it. Both feed `useAutomationWorkspace` for the workspace and its loading
 * state — an automation with no dedicated chat resolves to no workspace, which is expected: the
 * audience half then shows a note instead of controls, not a placeholder. Shows a small placeholder
 * while the reads are in flight, or once they settle with a dedicated chat whose workspace still
 * cannot be found, and otherwise renders the same `AutomationShareDialog` the detail page uses. A
 * read that failed says so: "no workspace" is an answer, and a 500 is not one.
 */
export function AutomationShareDialogById({
  automationId,
  conversationId,
  open,
  onOpenChange,
}: {
  automationId: string;
  conversationId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const projectionQuery = useQuery({
    ...automationProjectionQueryOptions(automationId),
    enabled: open,
  });
  const resolvedConversationId =
    conversationId !== undefined
      ? conversationId
      : (projectionQuery.data?.conversationId ?? null);
  const workspace = useAutomationWorkspace(
    open ? resolvedConversationId : undefined,
  );
  const settled = !projectionQuery.isLoading && !workspace.isLoading;
  const failed = projectionQuery.isError || workspace.failed;
  /** The projection answers `null` for an automation that is gone or no longer visible — a 404
   *  there is not an error, so without this the dialog would blame a missing workspace. */
  const gone = settled && projectionQuery.data === null;
  const hasDedicatedChat = resolvedConversationId !== null;
  const workspaceResolved = !hasDedicatedChat || workspace.workspaceId !== null;

  if (open && settled && projectionQuery.data && workspaceResolved) {
    return (
      <AutomationShareDialog
        automationId={automationId}
        workspaceId={workspace.workspaceId}
        conversationId={resolvedConversationId}
        visibility={projectionQuery.data.visibility}
        participantIds={projectionQuery.data.participantIds}
        open={open}
        onOpenChange={onOpenChange}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share automation</DialogTitle>
          <DialogDescription>
            {describeUnavailable(settled, failed, gone)}
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

/**
 * What the placeholder says while the dialog cannot open: still reading, a read that failed, an
 * automation that is gone, or a genuine answer of no workspace. Collapsing the middle two into the
 * last told an owner their automation had no workspace when the service blinked, and again when the
 * automation had been deleted out from under the row they clicked.
 */
function describeUnavailable(
  settled: boolean,
  failed: boolean,
  gone: boolean,
): string {
  if (failed) return 'Could not load this automation. Close and try again.';
  if (gone) return 'This automation is no longer available.';
  return settled ? 'This automation has no workspace' : 'Loading…';
}
